import { Card } from "@amzn/stencil-react-components/card";
import { Col, GridItem, GridItemProps, Hr, Row } from "@amzn/stencil-react-components/layout";
import { StarRating } from "@amzn/stencil-react-components/star-rating";
import { Text } from "@amzn/stencil-react-components/text";
import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { Link, LinkTextDecorationOptions } from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import { formatLastUsedDate } from "common/components/AIForMe/helpers/data-helpers";
import { StarAverageRating, StarAverageRatingSize } from "common/components/Ratings/StarAverageRating";
import { TrackClickInteraction } from "common/helpers";
import { useVoteSubmission, VoteHandler } from "common/hooks";
import { getString } from "common/uistringlabels/uiStringUtils";
import { AIForMeTool } from "lib/models/aiforme-models";
import { CatalogicalService } from "lib/services";
import { FeedbackModelRecord } from "lib/services/catalogical-models";
import React, { FC } from "react";

/**
 * Grid span configuration for responsive layout
 */
type GridSpan = {
  lg: GridItemProps["lg"];
  md: GridItemProps["md"];
};

/**
 * Extended AI tool model with last used date information
 */
export interface RecentlyUsedTool extends AIForMeTool {
  lastUsedDate: string;
}

/**
 * Props for the RecentlyUsedCard component
 */
export interface RecentlyUsedCardProps {
  tool: RecentlyUsedTool;
  index: number;
  voteHandler: VoteHandler;
  client: CatalogicalService<AIForMeTool>;
  gridSpan?: GridSpan;
  /** User's previous vote/rating for this tool */
  userVote: FeedbackModelRecord;
  /** Callback to track click interactions when user clicks "Learn More" */
  onTrackClick: TrackClickInteraction;
}

/**
 * Card component displaying a recently used AI tool with rating and interaction capabilities
 * @param props - Component props
 * @returns Rendered card component
 */
export const RecentlyUsedCard: FC<RecentlyUsedCardProps> = ({
  tool,
  index,
  voteHandler,
  client,
  gridSpan,
  userVote,
  onTrackClick,
}) => {
  const taxId = new TaxonomyIdItem(tool.taxonomyId);
  const linkTitle = `External link to Learn more about ${tool.title}`;

  const { rating, isSubmittingVote, errorMessage, itemFeedbackStats, handleVote } = useVoteSubmission({
    taxonomyId: tool.taxonomyId,
    voteHandler,
    client,
    previousRating: userVote?.rating,
    itemFeedback: tool.feedback,
  });

  return (
    <GridItem
      key={taxId.toString()}
      lg={gridSpan?.lg ?? 4}
      md={gridSpan?.md ?? 4}
      data-testid="RecentlyUsedCard"
      data-index={index}
    >
      <Card style={{ minWidth: "100%" }}>
        <Col gridGap={16} style={{ minWidth: "100%", justifyContent: "space-between" }}>
          <Col gridGap={8}>
            <Text
              data-testid={`RecentlyUsedCardTitle-${tool.title}`}
              fontSize="T300"
              fontWeight="bold"
              style={{ wordBreak: "break-word" }}
            >
              {tool.title}
            </Text>
            <StarAverageRating size={StarAverageRatingSize.Small} averageRating={itemFeedbackStats?.averageRating} />
            <Text fontSize="T100" color="neutral70" data-testid={`recently-used-card-last-used-${tool.title}`}>
              {getString("aiForMe.landingPage.recentlyUsedToolsSection.lastUsed", {
                lastUsedPeriod: formatLastUsedDate(tool.lastUsedDate),
              })}
            </Text>
          </Col>
          <Text
            data-testid={`recently-used-card-description-${tool.title}`}
            color="neutral70"
            style={{ overflow: "auto" }}
          >
            {tool.description}
          </Text>
          <Col style={{ marginTop: "auto" }}>
            <Hr />
            <Row alignItems="center" justifyContent="space-between" style={{ marginTop: "16px" }}>
              {isSubmittingVote && <Text fontSize="smaller">{getString("aiForMe.submitting")}</Text>}
              {errorMessage && <Text fontSize="smaller">{errorMessage}</Text>}
              {!isSubmittingVote && !errorMessage && (
                <Row alignItems="center" justifyContent="space-between" gridGap={0} padding={0}>
                  <Text fontSize="smaller" data-testid="quick-rate-label" margin={"0 8px 0 0"}>
                    {getString("aiForMe.quickRate")}
                  </Text>
                  <StarRating
                    name={`quick-rate-${tool.taxonomyId}`}
                    data-testid="quick-rate-star-rating"
                    value={rating}
                    onChange={handleVote}
                  />

                  <Link
                    style={{ marginRight: "S200" }}
                    data-testid={`recently-used-card-learn-more-${tool.title}`}
                    href={tool.learnMoreLink}
                    target="_blank"
                    title={linkTitle}
                    touchpoint="recently-used-card-learn-more"
                    customData={{
                      taxonomyId: taxId.toString(),
                      title: tool.title,
                    }}
                    onClick={() => onTrackClick(taxId)}
                    padding={"8px 24px"}
                    margin={"0 24px"}
                    textDecoration={LinkTextDecorationOptions.None}
                  >
                    {getString("aiForMe.learnMore")}
                  </Link>
                </Row>
              )}
            </Row>
          </Col>
        </Col>
      </Card>
    </GridItem>
  );
};
